import { ACTIVE_TOURNAMENT_SCHEMA_VERSION, type CareerRepository } from './CareerRepository';
import { TournamentFieldBuilder } from './TournamentFieldBuilder';
import { TournamentController } from './TournamentController';
import { TOUR_GOLFERS } from './TourGolfers';
import {
  captureLeaderboardSnapshot,
  PlayerCareerStakeEngine,
  TournamentForecastEngine,
  TournamentNarrativeEngine,
} from './TournamentBroadcast';
import type {
  ActiveCareerTournament,
  CareerCheckpointReason,
  CareerSave,
  CurrentHoleCheckpoint,
  SeasonEvent,
  TournamentRoundConditions,
} from './types';

const hash = (text: string) =>
  [...text].reduce((value, char) => Math.imul(value ^ char.charCodeAt(0), 16777619), 2166136261) >>>
  0;
const roundConditions = (event: SeasonEvent, seed: number): TournamentRoundConditions[] =>
  Array.from({ length: event.rounds }, (_, index) => {
    const value = (seed + (index + 1) * 2654435761) >>> 0;
    const minimum = event.windRange[0],
      range = event.windRange[1] - minimum;
    const speed = minimum + ((value % 1000) / 1000) * range;
    const angle = ((value >>> 10) % 628) / 100;
    return {
      windSeed: value,
      wind: { x: Math.sin(angle) * speed, y: 0, z: Math.cos(angle) * speed },
      greenSpeed: 9.5 + index * 0.35,
      firmness: event.firmness,
      pinSet: index + 1,
      teeSet: 'championship',
      timeOfDay: ['morning', 'afternoon', 'afternoon', 'golden-hour'][index],
    };
  });

/** Authoritative owner of a career tournament and its stable persistence checkpoints. */
export class CareerTournamentSession {
  private readonly controller = new TournamentController();
  constructor(
    private career: CareerSave,
    private readonly event: SeasonEvent,
    private readonly repository: CareerRepository,
  ) {}

  create(now = new Date().toISOString()): ActiveCareerTournament {
    if (this.career.activeTournamentState)
      throw new Error('Finish or abandon the active tournament first');
    if (
      this.career.eventHistory.some(
        (result) =>
          result.eventId === this.event.id && result.season === this.career.currentSeasonNumber,
      )
    )
      throw new Error('This event has already been completed');
    const fieldSeed = hash(
      `${this.career.careerId}:${this.career.currentSeasonNumber}:${this.event.id}`,
    );
    const field = new TournamentFieldBuilder().build(
      this.event,
      this.career,
      TOUR_GOLFERS,
      fieldSeed,
    );
    const state = this.controller.create(this.event, field.golfers, field.playerId, fieldSeed);
    state.playerPairing = state.pairings.find((pairing) => pairing.includes(field.playerId)) ?? [];
    const active: ActiveCareerTournament = {
      schemaVersion: ACTIVE_TOURNAMENT_SCHEMA_VERSION,
      eventId: this.event.id,
      courseId: this.event.courseId,
      tournamentState: state,
      playerRound: {
        roundNumber: 1,
        activeHoleNumber: 1,
        playerTotalToPar: 0,
        playerRoundToPar: 0,
        completedHoleScores: [],
      },
      pairedOpponentId: field.pairedOpponentId,
      fieldSeed,
      conditions: roundConditions(this.event, fieldSeed),
      checkpointRevision: 1,
      checkpointReason: 'tournamentCreated',
      startedAt: now,
      updatedAt: now,
      forecast: new TournamentForecastEngine().generate(
        this.event,
        TOUR_GOLFERS,
        this.career.worldHistory,
        this.career.golferForm,
        this.career.currentSeasonNumber,
      ),
      narrativeMemory: { recentlyShownKeys: [], golferMentionCounts: {}, categoryCooldowns: {} },
      priorHoleLeaderboardSnapshot: captureLeaderboardSnapshot(state, 1),
      simulatedHoleEvents: [],
    };
    this.career.playerCareerStakes = new PlayerCareerStakeEngine().generate(
      this.career,
      this.event,
    );
    this.career.activeTournamentState = active;
    this.career.seasonStatus =
      this.event.tier === 'seasonChampionship' ? 'championshipInProgress' : 'inProgress';
    this.repository.saveCareer(this.career);
    return active;
  }
  active(): ActiveCareerTournament {
    const active = this.career.activeTournamentState;
    if (!active || active.eventId !== this.event.id)
      throw new Error('No active tournament for this event');
    return active;
  }
  checkpoint(reason: CareerCheckpointReason, currentHole?: CurrentHoleCheckpoint) {
    const active = this.active();
    if (
      currentHole &&
      (!['resting', 'holed', 'idle'].includes(currentHole.playerBall.phase) ||
        !['resting', 'holed', 'idle'].includes(currentHole.opponentBall.phase))
    )
      return active; // preserve the previous stable checkpoint while either ball is in flight
    active.playerRound.currentHole = currentHole ?? active.playerRound.currentHole;
    active.checkpointReason = reason;
    active.checkpointRevision++;
    active.updatedAt = new Date().toISOString();
    this.repository.saveCareer(this.career);
    return active;
  }
  recordHole(playerScore: number, opponentScore: number, par: number) {
    const active = this.active(),
      state = active.tournamentState;
    const playerId = this.career.playerProfile.id;
    const before = active.priorHoleLeaderboardSnapshot;
    const events = [];
    this.controller.recordHole(state, playerId, playerScore, par);
    events.push({
      golferId: playerId,
      roundNumber: state.currentRound,
      holeNumber: active.playerRound.activeHoleNumber,
      score: playerScore,
      scoreToPar: playerScore - par,
      notable:
        playerScore - par <= -2
          ? ('eagle' as const)
          : playerScore - par === -1
            ? ('birdie' as const)
            : playerScore - par >= 2
              ? ('double' as const)
              : playerScore - par === 1
                ? ('bogey' as const)
                : null,
    });
    this.controller.recordHole(state, active.pairedOpponentId, opponentScore, par);
    // Advance the full field exactly once to the player's newly completed hole.
    // The stable tournament seed and golfer id make the result deterministic
    // across resume; currentHole prevents completed AI holes being resimulated.
    const targetHole = active.playerRound.completedHoleScores.length + 1;
    for (const entry of state.leaderboard) {
      if (entry.golferId === playerId || entry.golferId === active.pairedOpponentId) continue;
      if (entry.currentHole >= targetHole || entry.cutStatus === 'missedCut') continue;
      const seed = hash(
        `${state.simulationSeed}:${state.currentRound}:${targetHole}:${entry.golferId}`,
      );
      const roll = seed % 100;
      const delta = roll < 14 ? -1 : roll > 97 ? 2 : roll > 86 ? 1 : 0;
      const score = Math.max(1, par + delta);
      this.controller.recordHole(state, entry.golferId, score, par);
      events.push({
        golferId: entry.golferId,
        roundNumber: state.currentRound,
        holeNumber: targetHole,
        score,
        scoreToPar: delta,
        notable:
          delta <= -2
            ? ('eagle' as const)
            : delta === -1
              ? ('birdie' as const)
              : delta >= 2
                ? ('double' as const)
                : delta === 1
                  ? ('bogey' as const)
                  : null,
      });
    }
    active.playerRound.completedHoleScores.push(playerScore);
    active.playerRound.playerRoundToPar += playerScore - par;
    active.playerRound.playerTotalToPar = state.cumulativeScores[playerId];
    active.playerRound.activeHoleNumber = Math.min(18, active.playerRound.activeHoleNumber + 1);
    this.controller.updateProjectedCut(state, this.event);
    active.simulatedHoleEvents = events;
    active.latestBroadcast = new TournamentNarrativeEngine().generate(
      this.event.id,
      state,
      before,
      playerId,
      events,
      active.narrativeMemory,
    );
    return this.checkpoint('holeCompleted');
  }
}
